import './styles/appliancePage.scss';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { graphql } from 'gatsby';
import { useTranslation } from 'react-i18next';

import {
    ApplianceDescription,
    ApplianceDetails,
    ApplianceImage,
    ApplianceRetailers,
    ApplianceReviews,
    ApplianceSpecs,
    CompletePurchase,
} from '../components/appliance';
import ApplianceSingle from '../components/Listing/ApplianceSingle';
import { Breadcrumb } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import { FooterBanner } from '../components/Layout';
import { IAppliance } from '../interfaces';
import Layout from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Layout/Layout';
import SEO from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Seo';
import leftArrowIcon from '@progymedia/sbi-web-englander/src/images/englander-icons/Left-Short-Arrow-Icon.svg';
import rightArrowIcon from '@progymedia/sbi-web-englander/src/images/englander-icons/Right-Short-Arrow-Icon.svg';
import usaFlag from '@progymedia/sbi-web-englander/src/images/backgrounds/usa-flag-background.jpg';
import usaFlagMobile from '@progymedia/sbi-web-englander/src/images/backgrounds/usa-flag-background-mobile.jpg';
import useMobileDetect from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useMobileDetect';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useProductInventory } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/store';
import { ProductReviewsReview } from '@progymedia/gatsby-theme-boutik-ecommerce/src/models/productReviews';
import { useProductReviews } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useProductReviews';
import ApplianceManualsAndDocuments from '../components/appliance/ApplianceManualsAndDocuments';

export const query = graphql`
    query ($code: String!, $warrantyCms: String!) {
        appliance: productsJson(code: { eq: $code }) {
            code
            title {
                en_CA
                en_US
                fr_CA
            }
            title {
                en_CA
                en_US
                fr_CA
            }
            category
            documents {
                en_CA
                en_US
                fr_CA
                caption {
                    en_CA
                    en_US
                    fr_CA
                }
            }
            description {
                en_CA
                en_US
                fr_CA
                valuesMarkdownRemark {
                    en_CA
                    en_US
                    fr_CA
                }
            }
            store_retailers {
                code
            }
            online_retailers {
                code
            }
            slug {
                en_CA
                en_US
                fr_CA
            }
            images {
                en_CA
                en_US
                fr_CA
            }
            fields {
                ...productsFieldsImagesFragment
            }
            attributes {
                ...productAttributesFragment
                available_country {
                    values {
                        en_CA
                        en_US
                        fr_CA
                    }
                }
                suggested_appliances {
                    values {
                        en_CA
                        en_US
                        fr_CA
                    }
                }
                visao_id {
                    values {
                        en_CA
                    }
                }
                meta_title {
                    values {
                        en_CA
                        en_US
                        fr_CA
                    }
                }
                meta_description {
                    values {
                        en_CA
                        en_US
                        fr_CA
                    }
                }
            }
            options {
                code
                id
                images {
                    en_CA
                    en_US
                    fr_CA
                }
                title {
                    en_CA
                    en_US
                    fr_CA
                }
            }
        }
        allProducts: allProductsJson(
            filter: {
                category: { regex: "^/Englander/Heaters/" }
                status: { eq: "actif" }
            }
        ) {
            nodes {
                code
                slug {
                    en_CA
                    en_US
                    fr_CA
                }
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                category
                category_localized {
                    en_CA
                    en_US
                    fr_CA
                }
                attributes {
                    certifications {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    size {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
                images {
                    en_CA
                    en_US
                    fr_CA
                }
                fields {
                    ...productsFieldsImagesFragment
                }
            }
        }
        warrantyCms: allProductsJson(filter: { code: { eq: $warrantyCms } }) {
            nodes {
                documents {
                    en_CA
                    en_US
                    fr_CA
                }
            }
        }
    }
`;

export default function AppliancePage({ data, location }) {
    const appliance = data.appliance;
    const reviewsRef = useRef(null);
    const infoRef = useRef<HTMLDivElement>(null);

    const [filteredReviews, setFilteredReviews] = useState<
        ProductReviewsReview[]
    >([]);

    const inventory = useProductInventory(appliance.code);
    const specialPriceEnd =
        inventory?.[0]?.stock?.specialPriceEndDate?.split(' ')?.[0];

    const { currentLocale, navigateTranslated } = usePageLocalization();
    const warrantyDocument =
        data.warrantyCms?.nodes?.[0]?.documents?.[0][currentLocale];

    const productReviews = useProductReviews(appliance?.code);

    const metadata = {
        title: appliance?.attributes?.meta_title?.values[0],
        description: appliance?.attributes?.meta_description?.values[0],
    };
    const allProducts = data?.allProducts?.nodes;
    const isMobile = useMobileDetect().isMobile();
    const isTablet = useMobileDetect().isTablet();
    const recommandedData = appliance?.attributes?.suggested_appliances?.values;
    let recommandedProducts = [];
    if (recommandedData) {
        const ids = recommandedData
            ?.slice(0, 3)
            .map((e: any) => e?.[currentLocale]);
        const arr = allProducts.filter((prod: any) => ids.includes(prod?.code));
        recommandedProducts = arr;
    }
    const APPLIANCE_PER_PAGE = isTablet ? 2 : isMobile ? 1 : 3; // By default the max recommended product will be 3 for desktop.
    const SCREEN_SLIDER_TRIGGER = 100;
    const [page, setPage] = useState(1);
    const startSwipe = 0;
    const endSwipe = 0;

    const setNext = useCallback(() => {
        const pageNumber = Math.ceil(
            recommandedProducts.length / APPLIANCE_PER_PAGE
        );
        if (page !== pageNumber) setPage(page + 1);
        else setPage(1);
    }, [APPLIANCE_PER_PAGE, page, recommandedProducts.length]);
    const setPrevious = useCallback(() => {
        const pageNumber = Math.ceil(
            recommandedProducts.length / APPLIANCE_PER_PAGE
        );
        if (page !== 1) setPage(page - 1);
        else setPage(pageNumber);
    }, [APPLIANCE_PER_PAGE, page, recommandedProducts.length]);

    const handleSwipe = useCallback(() => {
        if (endSwipe - startSwipe > SCREEN_SLIDER_TRIGGER) {
            setPrevious();
        }
        if (endSwipe - startSwipe < -SCREEN_SLIDER_TRIGGER) {
            setNext();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endSwipe, startSwipe]);

    useEffect(() => {
        handleSwipe();
    }, [endSwipe, handleSwipe]);

    useEffect(() => {
        // Si l'appareil n'est pas disponible pour le pays actuel (available_country),
        // on redirige vers l'archive de sa catégorie
        if (
            (currentLocale.includes('CA') &&
                appliance?.attributes?.available_country?.values.every(
                    (value: { [x: string]: string }) =>
                        value[currentLocale] === 'United States'
                )) ||
            (currentLocale.includes('US') &&
                appliance?.attributes?.available_country?.values.every(
                    (value: { [x: string]: string }) =>
                        value[currentLocale] === 'Canada'
                ))
        ) {
            navigateTranslated(`${applianceType.link}/`);
        }
        // On veut seulement executer ce code lorsque la page termine de charger
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (productReviews?.reviews) {
            setFilteredReviews(productReviews?.reviews);
        }
    }, [productReviews?.reviews]);

    const { t } = useTranslation('layout');
    const applianceType = getApplianceType(appliance?.slug['en_CA'], t);

    const currentPathArray = location.pathname
        .split('/')
        .filter((path: any) => {
            // removes unwanted pathnames for breadcrumb navigation
            switch (path) {
                case '':
                    return false;
                case 'ca':
                    return false;
                case 'us':
                    return false;
                case 'fr':
                    return false;
                case 'en':
                    return false;
                default:
                    return true;
            }
        });
    const currentPath = currentPathArray.join('/');

    const recommandedAppliances =
        appliance?.attributes?.suggested_appliances?.values?.map(
            (recommandedAppliance: { [x: string]: IAppliance }) =>
                allProducts.find(
                    (product: any) =>
                        product.code === recommandedAppliance[currentLocale]
                )
        );
    const [recommandedApplianceIndex, setRecommandedApplianceIndex] =
        useState(0);
    const applianceCategory = appliance?.slug[currentLocale]
        ?.replace('/englander', '')
        ?.replace(appliance?.code.toLowerCase(), '');
    const [footerImg, setFooterImg] = useState(
        isMobile ? usaFlagMobile : usaFlag
    );

    useEffect(() => {
        if (isMobile) {
            setFooterImg(usaFlagMobile);
        } else {
            setFooterImg(usaFlag);
        }
    }, [isMobile]);

    return (
        <Layout>
            <SEO
                title={metadata?.title?.[currentLocale]}
                description={metadata?.description?.[currentLocale]}
            />
            <div className='container'>
                <Breadcrumb
                    elements={[
                        { name: t('appliance.home'), link: '/' },
                        {
                            name: applianceType.type,
                            link: applianceCategory,
                        },
                        {
                            name: `${appliance?.title?.[currentLocale]}`,
                            link: `${currentPath}/`,
                        },
                    ]}
                />
                <div className='appliance-page'>
                    <div className='appliance-details'>
                        <ApplianceImage
                            appliance={appliance}
                            currentLocale={currentLocale}
                        />

                        <ApplianceDetails
                            appliance={appliance}
                            reviewsRef={reviewsRef}
                            infoRef={infoRef}
                            specialPriceEnd={specialPriceEnd}
                        />
                    </div>

                    {appliance?.options.length > 0 && (
                        <CompletePurchase
                            options={appliance.options}
                            lang={currentLocale}
                        />
                    )}

                    <ApplianceDescription appliance={appliance} />
                    <ApplianceManualsAndDocuments
                        appliance={appliance}
                        warrantyDocument={warrantyDocument}
                    />
                    <ApplianceSpecs appliance={appliance} />

                    {((appliance?.store_retailers &&
                        appliance?.store_retailers.length > 0) ||
                        (appliance?.online_retailers &&
                            appliance?.online_retailers.length > 0)) && (
                        <ApplianceRetailers appliance={appliance} />
                    )}

                    <ApplianceReviews
                        code={appliance?.code}
                        ref={reviewsRef}
                        productReviews={productReviews}
                        filteredReviews={filteredReviews}
                        setFilteredReviews={setFilteredReviews}
                    />

                    {recommandedAppliances?.length > 0 && (
                        <div className='appliance-banner related-banner'>
                            {t('appliance.related-products')}
                        </div>
                    )}

                    {isMobile ? (
                        <div className='related-products mobile'>
                            {recommandedAppliances?.length > 0 && (
                                <>
                                    <img
                                        className='arrow left'
                                        src={leftArrowIcon}
                                        alt=''
                                        onClick={() => {
                                            if (
                                                recommandedApplianceIndex - 1 >=
                                                0
                                            ) {
                                                setRecommandedApplianceIndex(
                                                    recommandedApplianceIndex -
                                                        1
                                                );
                                            }
                                        }}
                                    />
                                    <ApplianceSingle
                                        appliance={
                                            recommandedAppliances?.[
                                                recommandedApplianceIndex
                                            ]
                                        }
                                    />
                                    <img
                                        className='arrow right'
                                        src={rightArrowIcon}
                                        alt=''
                                        onClick={() => {
                                            if (
                                                recommandedApplianceIndex + 1 <
                                                recommandedAppliances?.filter(
                                                    (recAppliance: any) =>
                                                        recAppliance
                                                ).length
                                            ) {
                                                setRecommandedApplianceIndex(
                                                    recommandedApplianceIndex +
                                                        1
                                                );
                                            }
                                        }}
                                    />
                                </>
                            )}
                        </div>
                    ) : (
                        <div className='related-products'>
                            {recommandedAppliances?.length > 0 &&
                                recommandedAppliances
                                    ?.filter(
                                        (recAppliance: any) => recAppliance
                                    )
                                    .map((recAppliance: IAppliance) => (
                                        <ApplianceSingle
                                            key={recAppliance?.code}
                                            appliance={recAppliance}
                                        />
                                    ))}
                        </div>
                    )}
                </div>
            </div>

            <FooterBanner
                image={footerImg}
                text={t('footer.proudly-made-us')}
            />
        </Layout>
    );
}
function getApplianceType(
    applianceType: string,
    t: any
): {
    type: string;
    link: string;
} {
    if (applianceType?.includes('wood-inserts')) {
        return {
            link: t('url:wood-inserts'),
            type: t('layout:menu-titles.wood-inserts'),
        };
    } else if (applianceType?.includes('wood-stoves')) {
        return {
            link: t('url:wood-stoves'),
            type: t('layout:menu-titles.wood-stoves'),
        };
    } else if (applianceType?.includes('pellet-stoves')) {
        return {
            link: t('url:pellet-stoves'),
            type: t('layout:menu-titles.appliance-pellet-stoves'),
        };
    } else if (applianceType?.includes('wood-furnaces')) {
        return {
            link: t('url:wood-furnaces'),
            type: t('layout:menu-titles.wood-furnaces'),
        };
    } else if (applianceType?.includes('cookstoves')) {
        return {
            link: t('url:cook-stoves'),
            type: t('layout:menu-titles.wood-cook-stoves'),
        };
    } else if (applianceType?.includes('outdoor')) {
        return {
            link: t('url:outdoor-fireplaces'),
            type: t('layout:menu-titles.outdoor-fireplaces'),
        };
    } else if (applianceType?.includes('gas-wall-mounted')) {
        return {
            link: t('url:wall-mounted-heaters'),
            type: t('layout:menu-titles.gas-wall-heaters'),
        };
    } else if (applianceType?.includes('camp-stoves')) {
        return {
            link: t('url:camp-stoves'),
            type: t('layout:menu-titles.camp-stoves'),
        };
    }
    return {
        type: t(`appliance.appliance`),
        link: '/',
    };
}
